
import api from './api';

export interface SearchFilters {
  category?: string;
  minRating?: number;
  minPrice?: number;
  maxPrice?: number;
  is24Hours?: boolean;
  isVerified?: boolean;
  isAvailable?: boolean;
}

export interface SearchParams {
  q?: string;
  lat?: number;
  lng?: number;
  radius?: number;
  category?: string;
  minRating?: number;
  minPrice?: number;
  maxPrice?: number;
  is24Hours?: boolean;
  isVerified?: boolean;
  isAvailable?: boolean;
  sortBy?: 'relevance' | 'distance' | 'rating' | 'price_low' | 'price_high' | 'popularity';
  page?: number;
  limit?: number;
}

export interface SearchResult {
  id: string;
  business_name: string;
  business_description: string;
  business_email: string;
  business_phone: string;
  business_website: string;
  whatsapp_number: string;
  landmark: string;
  address_line1: string;
  address_line2: string;
  town_id: string | null;
  county_id: string | null;
  postal_code: string;
  is_24_hours: boolean;
  verification_status: string;
  is_featured: boolean;
  average_rating: number;
  total_reviews: number;
  response_time: number;
  completion_rate: number;
  founded_year: number | null;
  employees: number | null;
  subscription_status: string;
  view_count: number;
  booking_count: number;
  certifications: any;
  payment_methods: any;
  languages: any;
  social_media: any;
  created_at: string;
  updated_at: string;
  latitude: number | null;
  longitude: number | null;
  distance_meters: number | null;
  relevance: number;
  user_first_name: string;
  user_last_name: string;
  user_profile_image: string | null;
  is_available: boolean;
  availability_message: string | null;
  primary_image: string | null;
  categories: Array<{
    id: string;
    name: string;
    slug: string;
    icon: string;
  }> | null;
  services: Array<{
    id: string;
    name: string;
    price: number;
    is_available: boolean;
  }> | null;
  tags: Array<{ name: string }> | null;
}

export interface SearchResponse {
  success: boolean;
  data: SearchResult[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  meta: {
    searchTerm: string | null;
    location: {
      latitude: number;
      longitude: number;
      radius: number;
    } | null;
    filters: SearchFilters;
    sortBy: string;
  };
}

// Matches the shape returned by getSearchSuggestions on the backend:
// providers, service_categories, and services all come back through
// this same union, distinguished by `type`.
export interface SearchSuggestion {
  id: string;
  name: string;
  type: 'provider' | 'category' | 'service';
  label: string;
  rating: number | null;
  is_24_hours: boolean | null;
}

export const searchService = {
  // Main search - converts params to proper query string
  searchProviders: async (params: SearchParams): Promise<SearchResponse> => {
    // Filter out undefined values
    const queryParams: Record<string, any> = {};
    
    if (params.q) queryParams.q = params.q;
    if (params.lat) queryParams.lat = params.lat;
    if (params.lng) queryParams.lng = params.lng;
    if (params.radius) queryParams.radius = params.radius;
    if (params.category) queryParams.category = params.category;
    if (params.minRating) queryParams.minRating = params.minRating;
    if (params.minPrice) queryParams.minPrice = params.minPrice;
    if (params.maxPrice) queryParams.maxPrice = params.maxPrice;
    if (params.is24Hours !== undefined) queryParams.is24Hours = params.is24Hours;
    if (params.isVerified !== undefined) queryParams.isVerified = params.isVerified;
    if (params.isAvailable !== undefined) queryParams.isAvailable = params.isAvailable;
    if (params.sortBy) queryParams.sortBy = params.sortBy;
    if (params.page) queryParams.page = params.page;
    if (params.limit) queryParams.limit = params.limit;

    console.log('🔍 Search service called with params:', queryParams);
    
    const response = await api.get<SearchResponse>('/search', { 
      params: queryParams 
    });
    
    console.log('📦 Search service response:', response.data);
    return response.data;
  },

  // Alias for search (backwards compatibility)
  search: (params: SearchParams): Promise<SearchResponse> => {
    return searchService.searchProviders(params);
  },

  // Get search suggestions (auto-complete)
  // NOTE: api.get() resolves to an AxiosResponse — the JSON body your
  // backend sent ({ success, data }) lives at response.data, not at
  // the top level. Must unwrap it here, same as searchProviders does,
  // or callers end up destructuring the wrong object.
  getSuggestions: async (q: string, limit: number = 10): Promise<{ success: boolean; data: SearchSuggestion[] }> => {
    const response = await api.get('/search/suggestions', { params: { q, limit } });
    return response.data;
  },

  // Get popular searches
  getPopularSearches: async (): Promise<{ success: boolean; data: Array<{ term: string; count: number }> }> => {
    const response = await api.get('/search/popular');
    return response.data;
  },

  // Get location from browser
  getCurrentLocation: (): Promise<{ latitude: number; longitude: number }> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          let errorMessage = 'Unable to get your location';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location permission denied. Please enable location services.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out. Please try again.';
              break;
          }
          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    });
  },
};